import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit, Trash2, Baby, DollarSign, RefreshCw } from "lucide-react";

const ExtrasPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    is_active: true
  });

  useEffect(() => {
    fetchExtras();
  }, []);

  const fetchExtras = async () => {
    try {
      setLoading(true);
      const response = await api.get("/extras?include_inactive=true");
      setExtras(response.data);
    } catch (error) {
      toast.error("Failed to load extras");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingExtra) {
        await api.put(`/extras/${editingExtra.id}`, payload);
        toast.success("Extra updated successfully");
      } else {
        await api.post("/extras", payload);
        toast.success("Extra created successfully");
      }

      setShowDialog(false);
      resetForm();
      fetchExtras();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save extra");
    }
  };

  const handleDelete = async (extraId) => {
    if (!confirm("Are you sure you want to delete this extra?")) return;
    
    try {
      await api.delete(`/extras/${extraId}`);
      toast.success("Extra deleted");
      fetchExtras();
    } catch (error) {
      toast.error("Failed to delete extra");
    }
  };

  const handleToggleActive = async (extra) => {
    try {
      await api.put(`/extras/${extra.id}`, { is_active: !extra.is_active });
      fetchExtras();
      toast.success(`Extra ${extra.is_active ? "disabled" : "enabled"}`);
    } catch (error) {
      toast.error("Failed to update extra");
    }
  };

  const openEditDialog = (extra) => {
    setEditingExtra(extra);
    setFormData({
      name: extra.name,
      description: extra.description || "",
      price: extra.price.toString(),
      is_active: extra.is_active
    });
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    setEditingExtra(null);
    resetForm();
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Manage Extras</h1>
              <p className="text-sm text-muted-foreground">Add-ons that customers can select when booking</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchExtras} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={openCreateDialog} data-testid="add-extra-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Extra
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Extras Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {extras.map(extra => (
            <Card key={extra.id} className={`dashboard-card ${!extra.is_active ? "opacity-60" : ""}`} data-testid={`extra-card-${extra.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Baby className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{extra.name}</h3>
                      <p className="text-sm text-muted-foreground">{extra.description}</p>
                    </div>
                  </div>
                  <Badge variant={extra.is_active ? "default" : "secondary"}>
                    {extra.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/10">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-500" />
                    <span className="text-2xl font-bold text-green-500">${extra.price.toFixed(2)}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(extra)} data-testid={`edit-${extra.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(extra.id)} data-testid={`delete-${extra.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                    <Switch
                      checked={extra.is_active}
                      onCheckedChange={() => handleToggleActive(extra)}
                      data-testid={`toggle-${extra.id}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {extras.length === 0 && (
          <div className="text-center py-12">
            <Baby className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No extras configured yet</p>
            <Button onClick={openCreateDialog} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Extra
            </Button>
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingExtra ? "Edit Extra" : "Add New Extra"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Baby Seat"
                data-testid="extra-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description"
                data-testid="extra-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Price ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="15.00"
                data-testid="extra-price-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                data-testid="extra-active-toggle"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-extra-btn">
                {editingExtra ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExtrasPage;
